import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useConfig } from "../lib/config";

// Tracking injection (GTM, Meta Pixel, GA4, custom <head> HTML).
// All IDs come from /admin/configuracion and are validated to prevent injection attacks.
// Scripts are inserted ONCE per session — repeated component renders don't duplicate them.
// SPA route changes manually fire PageView/page_view to keep analytics accurate.

const isSafeId = (s) => typeof s === "string" && /^[A-Za-z0-9_-]{1,40}$/.test(s);

// ---- One-shot script/snippet injectors (idempotent by element id) ----
const injectInlineScript = (id, code, target = "head") => {
  if (!code || document.getElementById(id)) return;
  const s = document.createElement("script");
  s.id = id;
  s.text = code;
  (target === "head" ? document.head : document.body).appendChild(s);
};

const injectAsyncScript = (id, src) => {
  if (!src || document.getElementById(id)) return;
  const s = document.createElement("script");
  s.id = id;
  s.src = src;
  s.async = true;
  document.head.appendChild(s);
};

const injectNoscriptToBody = (id, html) => {
  if (!html || document.getElementById(id)) return;
  const n = document.createElement("noscript");
  n.id = id;
  n.innerHTML = html; // safe: id-only inputs validated by isSafeId
  // Best practice: place GTM/Pixel noscript right after <body>
  if (document.body.firstChild) {
    document.body.insertBefore(n, document.body.firstChild);
  } else {
    document.body.appendChild(n);
  }
};

const injectCustomHeadHtml = (id, html) => {
  if (!html || document.getElementById(id)) return;
  const wrap = document.createElement("div");
  wrap.id = id;
  wrap.style.display = "none";
  wrap.innerHTML = html;
  document.head.appendChild(wrap);
  // Re-execute any inline <script> nodes so admin-pasted snippets actually run
  wrap.querySelectorAll("script").forEach((old) => {
    const s = document.createElement("script");
    [...old.attributes].forEach((a) => s.setAttribute(a.name, a.value));
    s.text = old.text;
    document.head.appendChild(s);
  });
};

const TrackingScripts = () => {
  const { config } = useConfig();
  const location = useLocation();

  const gtm = isSafeId((config.gtm_id || "").trim()) ? config.gtm_id.trim() : "";
  const pixel = isSafeId((config.meta_pixel_id || "").trim()) ? config.meta_pixel_id.trim() : "";
  const ga = isSafeId((config.ga4_id || "").trim()) ? config.ga4_id.trim() : "";
  const custom = config.head_scripts || "";
  const googleVerify = (config.google_verify_token || "").trim();
  const bingVerify = (config.bing_verify_token || "").trim();

  // ----- INITIAL INJECTION (one-time) -----
  useEffect(() => {
    // GTM — head snippet + body noscript fallback
    if (gtm) {
      injectInlineScript(
        "gtm-head",
        `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${gtm}');`,
        "head"
      );
      injectNoscriptToBody(
        "gtm-noscript",
        `<iframe src="https://www.googletagmanager.com/ns.html?id=${gtm}" height="0" width="0" style="display:none;visibility:hidden" title="gtm"></iframe>`
      );
    }

    // Meta Pixel — init runs once; PageView fires here for the initial page
    if (pixel) {
      injectInlineScript(
        "fb-pixel-head",
        `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${pixel}');fbq('track','PageView');`,
        "head"
      );
      injectNoscriptToBody(
        "fb-pixel-noscript",
        `<img height="1" width="1" style="display:none" src="https://www.facebook.com/tr?id=${pixel}&ev=PageView&noscript=1" alt=""/>`
      );
    }

    // GA4 — loader + config (sends initial page_view)
    if (ga) {
      injectAsyncScript("ga4-loader", `https://www.googletagmanager.com/gtag/js?id=${ga}`);
      injectInlineScript(
        "ga4-config",
        `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}window.gtag=gtag;gtag('js',new Date());gtag('config','${ga}');`,
        "head"
      );
    }

    // Custom admin HTML
    if (custom) {
      injectCustomHeadHtml("custom-head-block", custom);
    }

    // Search engine verification meta tags
    if (googleVerify) {
      const existing = document.querySelector('meta[name="google-site-verification"]');
      if (!existing) {
        const m = document.createElement("meta");
        m.name = "google-site-verification";
        m.content = googleVerify;
        document.head.appendChild(m);
      }
    }
    if (bingVerify) {
      const existing = document.querySelector('meta[name="msvalidate.01"]');
      if (!existing) {
        const m = document.createElement("meta");
        m.name = "msvalidate.01";
        m.content = bingVerify;
        document.head.appendChild(m);
      }
    }
  }, [gtm, pixel, ga, custom, googleVerify, bingVerify]);

  // ----- SPA ROUTE CHANGE: re-fire PageView / page_view (skip the very first run; initial fire already happens above) -----
  useEffect(() => {
    // Defer to next tick so the inline init scripts above run first
    const id = window.setTimeout(() => {
      const path = location.pathname + location.search;
      if (typeof window.fbq === "function") {
        try { window.fbq("track", "PageView"); } catch (e) { /* noop */ }
      }
      if (typeof window.gtag === "function" && ga) {
        try { window.gtag("event", "page_view", { page_path: path, page_location: window.location.href }); } catch (e) { /* noop */ }
      }
      // GTM virtual pageview
      if (window.dataLayer) {
        window.dataLayer.push({ event: "virtualPageview", page_path: path });
      }
    }, 0);
    return () => window.clearTimeout(id);
  }, [location.pathname, location.search, ga]);

  // ----- WHATSAPP LINK INTERCEPTOR: catch any <a href="https://wa.me/..."> or api.whatsapp.com clicks anywhere on the page and fire Contact + GA4 event -----
  useEffect(() => {
    const handler = (e) => {
      const anchor = e.target?.closest?.("a[href]");
      if (!anchor) return;
      const href = anchor.getAttribute("href") || "";
      if (!/wa\.me|api\.whatsapp\.com/i.test(href)) return;
      // Meta Pixel Contact
      if (typeof window.fbq === "function") {
        try { window.fbq("track", "Contact"); } catch (err) { /* noop */ }
      }
      // GA4 custom event
      if (typeof window.gtag === "function") {
        try { window.gtag("event", "whatsapp_click", { event_category: "contacto", link_url: href }); } catch (err) { /* noop */ }
      }
      // GTM dataLayer
      if (window.dataLayer) {
        window.dataLayer.push({ event: "whatsapp_click", event_category: "contacto", link_url: href });
      }
    };
    document.addEventListener("click", handler, { capture: true });
    return () => document.removeEventListener("click", handler, { capture: true });
  }, []);

  return null;
};

export default TrackingScripts;

// ---- Imperative helpers (kept for backwards compatibility with existing buttons) ----
export const fireConversion = (eventName = "Contact", payload = {}) => {
  if (typeof window === "undefined") return;
  if (typeof window.fbq === "function") {
    try { window.fbq("track", eventName, payload); } catch (err) { console.warn("[tracking] fbq failed:", err?.message); }
  }
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: eventName.toLowerCase(), ...payload });
  if (typeof window.gtag === "function") {
    try { window.gtag("event", eventName.toLowerCase(), payload); } catch (err) { console.warn("[tracking] gtag failed:", err?.message); }
  }
};
